/* ==========================================================================
   Cart tests — Control (C) vs "Recommended for you" (A) vs "Essentials only" (B)

   The control drawer component (assets/new-cart-drawer.js) owns open/close,
   quantity changes, the progress bar and the server-side re-render. This file
   adds only what the test needs on top:

     1. the upsell "+" buttons (§6a) — add one unit, then refresh the drawer
     2. a client-side arm switch so an A/B tool can pick control, recommended
        or minimal without a second theme, whatever the theme setting is

   snippets/new-cart-drawer.liquid renders the setting's drawer and ships the
   other one inert in <template data-cart-alt>. Switching swaps the two and
   re-runs the control component's reInitUI(), which re-binds its listeners.

   Listeners are delegated off <new-cart-drawer>, because refreshCartDrawer()
   replaces the drawer's innerHTML wholesale on every cart change.
   ========================================================================== */

(function () {
  'use strict';

  var VARIANTS = ['control', 'recommended', 'minimal'];

  function drawer() {
    return document.querySelector('new-cart-drawer');
  }

  /* ---- 1. Upsell add buttons ------------------------------------------- */

  async function addUpsell(button) {
    var variantId = Number(button.getAttribute('data-variant-id') || '0');
    if (!variantId || button.disabled) return;

    var sellingPlanId = Number(button.getAttribute('data-selling-plan-id') || '0');
    var payload = { id: variantId, quantity: 1 };
    if (sellingPlanId) payload.selling_plan = sellingPlanId;

    button.disabled = true;
    button.setAttribute('data-loading', 'true');

    try {
      var resp = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!resp.ok) {
        var body = await resp.json().catch(function () {
          return {};
        });
        throw new Error(body.description || body.message || 'Could not add that item.');
      }

      // Optimistic state so the check lands before the re-render.
      button.removeAttribute('data-loading');
      button.setAttribute('data-added', 'true');

      document.dispatchEvent(new CustomEvent('cart:update-counter'));
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } catch (error) {
      button.removeAttribute('data-loading');
      button.disabled = false;
      document.dispatchEvent(
        new CustomEvent('cart:error', { detail: { message: error.message } })
      );
    }
  }

  /* ---- 2. Client-side arm switch --------------------------------------- */

  /*
   * window.ab_cart_type is the Intelligems hook. A value set before this script
   * loads is picked up here; one set afterwards goes through the setter and
   * switches the drawer straight away.
   */
  var abCartType = window.ab_cart_type;
  try {
    Object.defineProperty(window, 'ab_cart_type', {
      configurable: true,
      get: function () {
        return abCartType;
      },
      set: function (next) {
        abCartType = next;
        applyVariant(drawer());
      },
    });
  } catch (e) {
    /* property locked by another script — the initial value still applies */
  }

  function readOverride() {
    try {
      var fromUrl = new URLSearchParams(window.location.search).get('cart_variant');
      if (VARIANTS.indexOf(fromUrl) !== -1) {
        window.sessionStorage.setItem('mk_cart_variant', fromUrl);
        return fromUrl;
      }
    } catch (e) {
      /* URLSearchParams or storage unavailable — fall through */
    }

    // Intelligems sets this in each arm ("Immediately" timing), usually before
    // this deferred script has loaded.
    if (VARIANTS.indexOf(abCartType) !== -1) {
      return abCartType;
    }

    if (VARIANTS.indexOf(window.MK_CART_TEST_VARIANT) !== -1) {
      return window.MK_CART_TEST_VARIANT;
    }

    try {
      var stored = window.sessionStorage.getItem('mk_cart_variant');
      if (VARIANTS.indexOf(stored) !== -1) return stored;
    } catch (e) {
      /* storage unavailable */
    }

    return null;
  }

  /**
   * Swap the live drawer markup with the one parked in <template data-cart-alt>.
   * The outgoing markup is parked in a new template, so it can come back.
   */
  function swapDrawer(root, toV2) {
    var template = root.querySelector(':scope > template[data-cart-alt]');
    if (!template) return;

    var parked = document.createElement('template');
    parked.setAttribute('data-cart-alt', toV2 ? 'control' : 'v2');
    Array.prototype.slice.call(root.childNodes).forEach(function (node) {
      if (node !== template) parked.content.appendChild(node);
    });

    root.replaceChildren(document.importNode(template.content, true), parked);
    root.classList.toggle('cartv2', toV2);

    // Fresh nodes: let the control component re-bind and redraw the bar.
    if (typeof root.reInitUI === 'function') root.reInitUI();
    if (typeof root.updateProgressBar === 'function') {
      root.updateProgressBar(parseInt(root.dataset.cartTotal || '0', 10) || 0);
    }
  }

  /**
   * Which drawer is live, read from the markup rather than the root's class:
   * refreshCartDrawer() swaps innerHTML but leaves the class alone.
   */
  function liveIsV2(root) {
    var template = root.querySelector(':scope > template[data-cart-alt]');
    return template ? template.getAttribute('data-cart-alt') === 'control' : root.classList.contains('cartv2');
  }

  function applyVariant(root) {
    if (!root) return;
    var active = readOverride() || root.getAttribute('data-cart-variant-default') || 'control';
    var wantsV2 = active !== 'control';
    var isV2 = liveIsV2(root);

    if (wantsV2 !== isV2) {
      swapDrawer(root, wantsV2);
    } else {
      root.classList.toggle('cartv2', isV2);
    }

    root.setAttribute('data-cart-variant', active);

    var reco = root.querySelector('[data-cartv2-reco]');
    if (reco) reco.hidden = active !== 'recommended';
  }

  /** Public hook so an A/B tool can switch variants at runtime. */
  window.setCartTestVariant = function (next) {
    if (VARIANTS.indexOf(next) === -1) return;
    try {
      window.sessionStorage.setItem('mk_cart_variant', next);
    } catch (e) {
      /* storage unavailable */
    }
    window.MK_CART_TEST_VARIANT = next;
    abCartType = next;
    applyVariant(drawer());
  };

  /* ---- Wiring ----------------------------------------------------------- */

  function bind(root) {
    if (!root || root.getAttribute('data-cartv2-bound') === 'true') return;

    root.addEventListener('click', function (event) {
      var button = event.target.closest('[data-cartv2-add]');
      if (!button || !root.contains(button)) return;
      event.preventDefault();
      addUpsell(button);
    });

    // refreshCartDrawer() swaps innerHTML, so re-apply the variant after it.
    new MutationObserver(function () {
      applyVariant(root);
    }).observe(root, { childList: true });

    root.setAttribute('data-cartv2-bound', 'true');
    applyVariant(root);
  }

  function init() {
    bind(drawer());
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
